import {
  type FC,
  type MouseEvent as ReactMouseEvent,
  memo,
  useCallback,
  useMemo,
} from "react";
import ReactFlow, {
  Background,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import type { PlanSnapshotNodeWithDependencyCounts } from "../../types";

import { Badge } from "../common";
import { ContentCard } from "../common/ContentCard";
import { GraphContainer } from "../common/GraphContainer";
import { riskVariant, statusVariant } from "./badgeVariants";

interface PlanSnapshotDagViewProps {
  nodes: PlanSnapshotNodeWithDependencyCounts[];
  nodeById: Map<string, PlanSnapshotNodeWithDependencyCounts>;
  nodeByIssueKey: Map<string, PlanSnapshotNodeWithDependencyCounts>;
  onSelectNode: (nodeId: string) => void;
}

interface DagNodeData {
  node: PlanSnapshotNodeWithDependencyCounts;
}

const NODE_WIDTH = 220;
const X_SPACING = 260;
const Y_SPACING = 160;

const DagNode: FC<NodeProps<DagNodeData>> = memo(({ data }) => {
  const { node } = data;
  const label =
    node.title ?? node.humanized_description ?? node.description ?? "Untitled";

  return (
    <ContentCard
      className="cursor-pointer border border-l-[3px] border-slate-200 bg-white transition-colors hover:border-l-emerald-500 dark:border-slate-700 dark:bg-arti-panel dark:hover:border-l-emerald-500"
      style={{ width: NODE_WIDTH }}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2 !w-2 !rounded-none !border-none !bg-slate-300 dark:!bg-slate-600"
      />
      <div className="flex items-center justify-between gap-1.5">
        <span className="truncate font-mono text-xs font-bold text-slate-900 dark:text-white">
          {node.issue_key}
        </span>
        {node.status ? (
          <Badge
            size="sm"
            variant={statusVariant(node.status)}
            className="origin-right scale-75"
          >
            {node.status}
          </Badge>
        ) : null}
      </div>
      <div className="mt-1 line-clamp-2 font-mono text-[10px] leading-tight text-slate-500 dark:text-slate-400">
        {label}
      </div>
      {node.self_assessed_risk ? (
        <div className="mt-1.5">
          <Badge
            size="sm"
            variant={riskVariant(node.self_assessed_risk)}
            className="origin-left scale-75"
          >
            Risk: {node.self_assessed_risk}
          </Badge>
        </div>
      ) : null}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-2 !w-2 !rounded-none !border-none !bg-slate-300 dark:!bg-slate-600"
      />
    </ContentCard>
  );
});

const nodeTypes = { dag: DagNode };

function buildLevelMap(
  nodes: PlanSnapshotNodeWithDependencyCounts[],
  nodeById: Map<string, PlanSnapshotNodeWithDependencyCounts>,
  nodeByIssueKey: Map<string, PlanSnapshotNodeWithDependencyCounts>,
): Map<string, number> {
  const levels = new Map<string, number>();

  function resolveDepId(
    dep: PlanSnapshotNodeWithDependencyCounts["dependencies"][number],
  ): string | undefined {
    if (dep.depends_on_node_id && nodeById.has(dep.depends_on_node_id)) {
      return dep.depends_on_node_id;
    }
    return nodeByIssueKey.get(dep.depends_on_key)?.node_id;
  }

  function getLevel(nodeId: string, visited: Set<string>): number {
    const cached = levels.get(nodeId);
    if (cached !== undefined) {
      return cached;
    }
    if (visited.has(nodeId)) {
      return 0;
    }

    visited.add(nodeId);
    const node = nodeById.get(nodeId);
    if (!node || node.dependencies.length === 0) {
      levels.set(nodeId, 0);
      return 0;
    }

    let maxDepLevel = -1;
    for (const dep of node.dependencies) {
      const depNodeId = resolveDepId(dep);
      if (depNodeId) {
        maxDepLevel = Math.max(maxDepLevel, getLevel(depNodeId, visited));
      }
    }

    const level = maxDepLevel >= 0 ? maxDepLevel + 1 : 0;
    levels.set(nodeId, level);
    return level;
  }

  for (const node of nodes) {
    getLevel(node.node_id, new Set<string>());
  }
  return levels;
}

function layoutDag(
  nodes: PlanSnapshotNodeWithDependencyCounts[],
  nodeById: Map<string, PlanSnapshotNodeWithDependencyCounts>,
  nodeByIssueKey: Map<string, PlanSnapshotNodeWithDependencyCounts>,
): { nodes: Node<DagNodeData>[]; edges: Edge[] } {
  const levels = buildLevelMap(nodes, nodeById, nodeByIssueKey);

  // Group by level
  const byLevel = new Map<number, PlanSnapshotNodeWithDependencyCounts[]>();
  for (const node of nodes) {
    const level = levels.get(node.node_id) ?? 0;
    const group = byLevel.get(level);
    if (group) {
      group.push(node);
    } else {
      byLevel.set(level, [node]);
    }
  }

  // Build positioned nodes
  const graphNodes: Node<DagNodeData>[] = [];
  for (const [level, levelNodes] of byLevel) {
    const rowWidth = levelNodes.length * X_SPACING;
    const startX = -rowWidth / 2;
    for (let i = 0; i < levelNodes.length; i++) {
      const node = levelNodes[i];
      graphNodes.push({
        data: { node },
        id: node.node_id,
        position: { x: startX + i * X_SPACING, y: level * Y_SPACING },
        type: "dag",
      });
    }
  }

  // Build edges
  const edges: Edge[] = [];
  for (const node of nodes) {
    for (const dep of node.dependencies) {
      const depNodeId =
        (dep.depends_on_node_id && nodeById.has(dep.depends_on_node_id)
          ? dep.depends_on_node_id
          : undefined) ?? nodeByIssueKey.get(dep.depends_on_key)?.node_id;

      if (!depNodeId) {
        continue;
      }

      edges.push({
        id: `${depNodeId}-${node.node_id}`,
        markerEnd: { type: MarkerType.ArrowClosed },
        source: depNodeId,
        style: { stroke: "#9ca3af", strokeWidth: 2 },
        target: node.node_id,
        type: "smoothstep",
      });
    }
  }

  return { edges, nodes: graphNodes };
}

export const PlanSnapshotDagView: FC<PlanSnapshotDagViewProps> = ({
  nodes,
  nodeById,
  nodeByIssueKey,
  onSelectNode,
}) => {
  const layout = useMemo(
    () => layoutDag(nodes, nodeById, nodeByIssueKey),
    [nodes, nodeById, nodeByIssueKey],
  );

  const onNodeClick = useCallback(
    (_: ReactMouseEvent, node: Node<DagNodeData>) => {
      onSelectNode(node.data.node.node_id);
    },
    [onSelectNode],
  );

  return (
    <div className="px-3 lg:px-8">
      <GraphContainer
        height="calc(100vh - 300px)"
        className="overflow-hidden border-2 border-slate-300 bg-slate-50 shadow-inner dark:border-slate-700 dark:bg-arti-background"
      >
        <ReactFlow
          nodes={layout.nodes}
          edges={layout.edges}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-right"
          onNodeClick={onNodeClick}
          nodesDraggable={false}
        >
          <Background color="#94a3b8" gap={20} size={1} />
          <Controls className="!border-slate-300 !bg-white !p-1 !shadow-sm dark:!border-slate-700 dark:!bg-slate-800" />
        </ReactFlow>
      </GraphContainer>
    </div>
  );
};
